import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Link, Typography, Paper } from '@mui/material';
import { api, Sponsor } from '../services/api';
import { useLanguage } from '../translations/LanguageContext';

const Footer: React.FC = () => {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const mounted = useRef(false);
  const { translations } = useLanguage();

  useEffect(() => {
    // Prevent double fetch in strict mode
    if (mounted.current) return;
    mounted.current = true;

    const fetchSponsors = async () => {
      try {
        const data = await api.getSponsors();
        setSponsors(data);
      } catch (err) {
        console.error('Failed to fetch sponsors:', err);
      }
    };

    fetchSponsors();
  }, []); // Empty dependency array as we're using ref for control

  if (sponsors.length === 0) {
    return null;
  }

  return (
    <Paper
      component="footer"
      elevation={0}
      sx={{
        mt: 'auto',
        py: 3,
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {translations.footer.supportedBy}
            </Typography>
          
            {sponsors.map((sponsor) => (
              <Link
                key={sponsor.id}
                href={sponsor.website_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                {sponsor.logo_url ? (
                  <img
                    src={sponsor.logo_url}
                    alt={`${sponsor.name} logo`}
                    style={{
                      height: '40px',
                      width: 'auto',
                      maxWidth: '120px',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {sponsor.name}
                  </Typography>
                )}
              </Link>
            ))}
          </Box>
        </Box>
      </Container>
    </Paper>
  );
};

export default Footer; 